// The sections, as data.
//
// Same shape as `dashboard/cards.tsx` and for the same reason: `Billing.tsx`
// knows nothing about what is on it, so a new section is an entry here plus a
// key in `workspace_usage()` — no new route, no second request, and no change
// to the screen that lays them out.
//
// `when` is what keeps this screen short for a workspace that has never touched
// the studio. An absent section beats an empty one: a card reading "0 credits
// of 0" teaches nobody anything, and four of them make the two numbers that
// matter harder to find.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { formatLimit } from "../lib/entitlements";
import type { UsageMeter, WorkspaceUsage } from "./types";

export interface BillingSection {
  id: string;
  title: string;
  /** One line under the title, in the reader's terms rather than the table's. */
  lede?: (u: WorkspaceUsage) => ReactNode;
  /** False when there is nothing worth a section. */
  when?: (u: WorkspaceUsage) => boolean;
  size?: "full" | "half";
  render: (u: WorkspaceUsage) => ReactNode;
}

const nf = new Intl.NumberFormat();

const date = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    day: "numeric", month: "long", year: "numeric",
  });

/** "in 6 days" / "tomorrow" / "today" — the shape a deadline is read in. */
function countdown(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "in the past";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

// ---------------------------------------------------------------- the meter

/**
 * One meter, with its number written out beside it.
 *
 * THE BAR IS NEVER THE ONLY STATEMENT. It is `aria-hidden` and the text next to
 * it says the same thing, because a bar at 94% and a bar at 100% are four
 * pixels apart and only one of them means "the next upload will be refused".
 *
 * `limit === null` is UNLIMITED and gets no bar at all. Drawing a full bar for
 * it would say the opposite of what is true, and drawing an empty one invites
 * the reader to work out a percentage of infinity.
 */
function MeterRow({ meter }: { meter: UsageMeter }) {
  const { used, limit, unit } = meter;

  if (!meter.enabled) {
    return (
      <div className="bill-meter" data-tone="off">
        <span className="bill-meter-name">{meter.name}</span>
        <span className="bill-meter-text muted">
          Not included on this plan
        </span>
      </div>
    );
  }

  if (limit === null) {
    return (
      <div className="bill-meter" data-tone="ok">
        <span className="bill-meter-name">{meter.name}</span>
        <span className="bill-meter-text">
          {formatLimit(used, unit)} used <span className="muted">· unlimited</span>
        </span>
      </div>
    );
  }

  const pct = limit === 0 ? 100 : Math.min(100, (used / limit) * 100);
  // Three states, and the thresholds are deliberate: `full` is the one that
  // changes what the product does next, so it is the only one painted as an
  // error. 80% is a nudge, not a problem.
  const tone = used >= limit ? "full" : pct >= 80 ? "near" : "ok";

  return (
    <div className="bill-meter" data-tone={tone}>
      <span className="bill-meter-name">
        {meter.name}
        {meter.resets_monthly && <span className="bill-chip">monthly</span>}
      </span>
      <span className="bill-meter-track" aria-hidden="true">
        <span className="bill-meter-fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="bill-meter-text">
        {formatLimit(used, unit)} of {formatLimit(limit, unit)}
        {tone === "full" && <strong className="bill-meter-flag"> — full</strong>}
      </span>
    </div>
  );
}

/** A headline number and what it is. No trend: this is a bill, not a report. */
function Stat({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  return (
    <div className="bill-stat">
      <span className="bill-stat-label">{label}</span>
      <span className="bill-stat-value">{value}</span>
      {hint && <span className="bill-stat-hint muted">{hint}</span>}
    </div>
  );
}

const metersFor = (u: WorkspaceUsage, keys: string[]) =>
  keys
    .map((k) => u.meters.find((m) => m.key === k))
    .filter((m): m is UsageMeter => !!m && m.implemented);

/** Everything metered that the two curated groups below did not claim. */
const otherMeters = (u: WorkspaceUsage) => {
  const claimed = new Set([...MONTHLY_KEYS, ...LEVEL_KEYS]);
  return u.meters.filter((m) => m.implemented && !claimed.has(m.key));
};

const MONTHLY_KEYS = ["posts_per_month", "ai_credits_per_month"];
const LEVEL_KEYS = ["connections", "cloud_storage_bytes"];

// ---------------------------------------------------------------- sections

export const SECTIONS: BillingSection[] = [
  {
    id: "plan",
    title: "Your plan",
    size: "full",
    render: (u) => {
      const plan = u.plan;
      const sub = u.subscription;
      const price =
        !plan || plan.price_cents === null || plan.price_cents === 0
          ? "Free"
          : `${(plan.price_cents / 100).toLocaleString(undefined, {
              style: "currency",
              currency: plan.currency,
              maximumFractionDigits: plan.price_cents % 100 === 0 ? 0 : 2,
            })} / ${plan.billing_period}`;

      return (
        <>
          <div className="bill-stats">
            <Stat label="Plan" value={plan?.name ?? "Free"} hint={price} />
            <Stat
              label="Status"
              value={sub ? sub.status : "active"}
              hint={sub ? `since ${date(sub.started_at)}` : "on the free defaults"}
            />
            {sub?.current_period_end && (
              <Stat
                label="Paid up to"
                value={date(sub.current_period_end)}
                hint={countdown(sub.current_period_end)}
              />
            )}
            <Stat
              label="People"
              value={nf.format(u.activity.members)}
              hint={`${nf.format(u.activity.connections)} connected accounts`}
            />
          </div>

          {/* Not a footnote. Somebody reading "paid up to 14 October" will
              assume a card gets charged on the 14th, and nothing does. */}
          {sub?.current_period_end && !sub.renews_automatically && (
            <p className="bill-note">
              <strong>This does not renew by itself.</strong> There is no card on file and no
              checkout — when a plan is due, someone from our side arranges it with you. Ask for a
              change from <Link to="/plans">Pricing</Link> and we will pick it up.
            </p>
          )}

          {sub?.cancel_at && (
            <p className="bill-note warn">
              This plan is set to end on <strong>{date(sub.cancel_at)}</strong> ({countdown(sub.cancel_at)}).
            </p>
          )}
        </>
      );
    },
  },

  {
    id: "monthly",
    title: "This month",
    lede: (u) =>
      `Resets ${countdown(u.range.month_resets_at)}, on ${date(u.range.month_resets_at)}.`,
    when: (u) => metersFor(u, MONTHLY_KEYS).length > 0,
    size: "half",
    render: (u) => (
      <>
        <div className="bill-meters">
          {metersFor(u, MONTHLY_KEYS).map((m) => (
            <MeterRow key={m.key} meter={m} />
          ))}
        </div>
        {/* The window picker at the top changes the charts and NOT these two.
            Without this line, a reader on "7 days" reasonably concludes they
            have spent 40 credits this week when it is 40 this month. */}
        <p className="bill-note quiet">
          These two are counted over the calendar month in UTC — the same clock the plan is
          enforced on — so they do not follow the range selected above.
        </p>
      </>
    ),
  },

  {
    id: "levels",
    title: "What you are holding",
    lede: () => "Not a monthly allowance: these go up and down as you add and remove things.",
    when: (u) => metersFor(u, LEVEL_KEYS).length > 0,
    size: "half",
    render: (u) => (
      <div className="bill-meters">
        {metersFor(u, LEVEL_KEYS).map((m) => (
          <MeterRow key={m.key} meter={m} />
        ))}
      </div>
    ),
  },

  {
    id: "ai",
    title: "AI drafting",
    lede: (u) =>
      u.ai.byok_calls > 0
        ? `${nf.format(u.ai.plan_calls)} drafts on your plan's credits, ${nf.format(u.ai.byok_calls)} on your own provider key.`
        : `${nf.format(u.ai.generations)} drafts in this window.`,
    // Nothing to say to a workspace that has never opened the studio, and a
    // row of zeroes would only make the meters above harder to find.
    when: (u) => u.ai.generations > 0 || u.ai_month.credits_spent > 0,
    size: "full",
    render: (u) => (
      <>
        <div className="bill-stats">
          <Stat
            label="Credits this month"
            value={nf.format(u.ai_month.credits_spent)}
            hint="What the monthly allowance above is measured against."
          />
          <Stat
            label="Drafts in this window"
            value={nf.format(u.ai.generations)}
            hint={u.ai.failed > 0 ? `${nf.format(u.ai.failed)} failed and cost nothing` : "none failed"}
          />
          {u.ai.byok_calls > 0 && (
            <Stat
              label="On your own key"
              value={nf.format(u.ai.byok_calls)}
              hint="Billed by your provider, not by us. These spend no credits."
            />
          )}
          <Stat
            label="Tokens"
            value={nf.format(u.ai.tokens_in + u.ai.tokens_out)}
            hint={
              u.ai.tokens_cached > 0
                ? `${nf.format(u.ai.tokens_cached)} came from cache`
                : "in and out combined"
            }
          />
        </div>

        {u.by_member.length > 0 && (
          <>
            <h3 className="bill-subhead">Who has been drafting</h3>
            <div className="table-scroll">
              <table className="bill-table">
                <thead>
                  <tr>
                    <th scope="col">Person</th>
                    <th scope="col" className="num">Drafts</th>
                    <th scope="col" className="num">Credits</th>
                    <th scope="col" className="num">Own key</th>
                  </tr>
                </thead>
                <tbody>
                  {u.by_member.map((m) => (
                    <tr key={m.user_id}>
                      <th scope="row">{m.email ?? "a member of this workspace"}</th>
                      <td className="num">{nf.format(m.generations)}</td>
                      <td className="num">{nf.format(m.credits_spent)}</td>
                      <td className="num">
                        {m.byok_calls > 0 ? nf.format(m.byok_calls) : <span className="muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Counts only, and saying so matters: an admin looking at this
                should not have to wonder whether we also showed them what
                their colleagues wrote. */}
            <p className="bill-note quiet">
              How much, never what. Nothing anyone wrote or generated is on this screen.
            </p>
          </>
        )}
      </>
    ),
  },

  {
    id: "storage",
    title: "Media and transfer",
    lede: () => "What is stored, and what has been served out of it.",
    size: "full",
    render: (u) => (
      <>
        <div className="bill-stats">
          <Stat
            label="Stored"
            value={formatLimit(u.storage.bytes, "bytes")}
            hint={`${nf.format(u.storage.objects)} files`}
          />
          <Stat
            label="Served — measured"
            value={formatLimit(u.transfer.measured_bytes, "bytes")}
            hint="Transfers we watched complete."
          />
          <Stat
            label="Served — estimated"
            value={formatLimit(u.transfer.attributed_bytes, "bytes")}
            hint="Files a browser or a network was given a link to."
          />
        </div>

        {/* The two transfer figures are never added, and the reason has to be
            on the screen rather than in a doc: one is a measurement and the
            other is a ceiling, and their sum is neither. */}
        <p className="bill-note quiet">
          The two transfer figures are different kinds of number and are deliberately not added
          together. <strong>Measured</strong> is a transfer whose far end confirmed it finished.{" "}
          <strong>Estimated</strong> is a file we handed out a link to — a preview in your library,
          or a network collecting a post's image — priced at the file's full size, which is the most
          it could have cost.
        </p>

        {!u.transfer.has_any ? (
          <p className="bill-note">
            No transfer has been recorded for this workspace yet. That is not the same as none
            happening — recording it is new, so an older workspace can show nothing here while
            still having served plenty.
          </p>
        ) : !u.transfer.is_billed ? (
          <p className="bill-note quiet">
            Transfer is not charged for on any plan. It is here because it is your data and you are
            the only person who can act on it.
          </p>
        ) : null}

        {u.storage.counts_rows_not_bucket && (
          <p className="bill-note quiet">
            Stored counts the files in your library. It is the same figure your plan's storage limit
            is enforced against, so the two can never disagree.
          </p>
        )}
      </>
    ),
  },

  {
    id: "limits",
    title: "Your other limits",
    lede: () => "Caps on a single thing rather than on a total. Nothing is consumed here.",
    when: (u) => otherMeters(u).length > 0,
    size: "full",
    render: (u) => (
      <div className="table-scroll">
        <table className="bill-table">
          <thead>
            <tr>
              <th scope="col">Limit</th>
              <th scope="col">On your plan</th>
            </tr>
          </thead>
          <tbody>
            {otherMeters(u).map((m) => (
              <tr key={m.key}>
                <th scope="row">
                  {m.name}
                  {m.description && <span className="muted bill-desc">{m.description}</span>}
                </th>
                <td>
                  {!m.enabled ? (
                    <span className="muted">Not included</span>
                  ) : (
                    formatLimit(m.limit, m.unit)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },

  {
    id: "activity",
    title: "What it produced",
    lede: () => "The other side of the ledger, over the range selected above.",
    size: "full",
    render: (u) => (
      <div className="bill-stats">
        <Stat label="Posts written" value={nf.format(u.activity.posts_created)} />
        <Stat
          label="Deliveries published"
          value={nf.format(u.activity.deliveries_published)}
          hint="One post to three networks is three."
        />
        <Stat label="Files uploaded" value={nf.format(u.activity.media_uploaded)} />
      </div>
    ),
  },
];
